import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { usersService } from 'src/app/services/services/users.service';
import { UserGroup, UsersUsergroupsModel } from '../../models';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';

export interface AddUsergroupDialogData {
  model: UsersUsergroupsModel;
}

@Component({
  selector: 'app-add-usergroup-dialog',
  templateUrl: './add-usergroup-dialog.component.html',
  styleUrls: ['./add-usergroup-dialog.component.scss'],
  standalone: false
})
export class AddUsergroupDialogComponent implements OnInit {
  usergroupName: string = '';
  optionType: 'GroupType' | 'BasedOnGroup' = 'GroupType';
  selectedGroupType: number | null = null;
  selectedBasedOnGroup: number | null = null;
  errorMessage: string = '';
  isSaving: boolean = false;
  groupTypeOptions: { value: number; label: string }[] = [];
  userGroupOptions: { value: number; label: string }[] = [];


  groupTypes: { [key: number]: string } = {};
  userGroups: UserGroup[] = [];

  constructor(
    public dialogRef: MatDialogRef<AddUsergroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddUsergroupDialogData,
    private usersService: usersService,
    private i18n: I18NextPipe,
    private dialog: MatDialog,
  ) {
    if (data?.model) {
      this.groupTypes = data.model.groupTypes || {};
      this.userGroups = data.model.allUserGroupsList || [];

      this.groupTypeOptions = Object.keys(this.groupTypes).map(key => ({
        value: Number(key),
        label: this.groupTypes[Number(key)]
      }));
      this.userGroupOptions = this.userGroups.map(ug => ({
        value: ug.id,
        label: ug.name
      }));
    }
  }

  ngOnInit(): void {
    // Load user groups list if not already loaded
    if (this.userGroups.length === 0) {
      this.usersService.getUserGroupList()
        .subscribe({
          next: (groups: UserGroup[]) => {
            this.userGroups = groups;
          },
          error: () => {
            this.errorMessage = this.i18n.transform('users:usergroups_err_getUsergroupsFailed',
              { defaultValue: 'Error loading user groups' }) as string;
          }
        });
    }
  }

  onOptionChange(option: 'GroupType' | 'BasedOnGroup'): void {
    this.optionType = option;
    if (option === 'GroupType') {
      this.selectedBasedOnGroup = null;
    } else {
      this.selectedGroupType = null;
    }
    this.updateButtonState();
  }

  onNameChange(): void {
    this.updateButtonState();
  }

  onGroupTypeChange(): void {
    this.updateButtonState();
  }

  onBasedOnGroupChange(): void {
    this.updateButtonState();
  }

  updateButtonState(): void {
    // Button state is handled in template via [disabled] binding
  }

  canSave(): boolean {
    const hasName = this.usergroupName.trim().length > 0;
    const hasSelection =
      (this.optionType === 'GroupType' && this.selectedGroupType > 0) ||
      (this.optionType === 'BasedOnGroup' && this.selectedBasedOnGroup > 0);
    return hasName && hasSelection && !this.isSaving;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    if (!this.canSave()) {
      return;
    }

    this.errorMessage = '';
    this.isSaving = true;

    // Check if name is available
    this.usersService.getUserGroupList()
      .subscribe({
        next: (groups: UserGroup[]) => {
          const nameExists = groups.some(g => g.name === this.usergroupName.trim());

          if (nameExists) {
            this.errorMessage = this.i18n.transform('users:usergroups_err_usergroupNameTaken',
              { defaultValue: 'This user group name is already in use' }) as string;
            this.isSaving = false;
            return;
          }

          // Create the user group
          if (this.optionType === 'GroupType') {
            this.createByGroupType();
          } else {
            this.createBasedOnGroup();
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('users:usergroups_err_getUsergroupsFailed',
            { defaultValue: 'Error checking user group name' }) as string;
          this.isSaving = false;
        }
      });
  }

  private createByGroupType(): void {
    this.usersService.createNewUsergroup(this.usergroupName.trim(), this.selectedGroupType)
      .subscribe({
        next: (success: boolean) => {
          if (success) {
            this.dialogRef.close({ success: true, reload: true });
          } else {
            this.errorMessage = this.i18n.transform('users:validate_illegalGroupName',
              { defaultValue: 'Invalid group name' }) as string;
            this.isSaving = false;
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('users:usergroups_err_makeUsergroupFailed',
            { defaultValue: 'Error creating user group' }) as string;
          this.isSaving = false;
        }
      });
  }

  private createBasedOnGroup(): void {
    this.usersService.createUserGroupBasedOnUserGroup(this.usergroupName.trim(), this.selectedBasedOnGroup)
      .subscribe({
        next: (success: boolean) => {
          if (success) {
            this.dialogRef.close({ success: true, reload: true });
          } else {
            this.errorMessage = this.i18n.transform('users:validate_illegalGroupName',
              { defaultValue: 'Invalid group name' }) as string;
            this.isSaving = false;
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('users:usergroups_err_makeUsergroupFailed',
            { defaultValue: 'Error creating user group' }) as string;
          this.isSaving = false;
        }
      });
  }

  openHelp(): void {
                this.dialog.open(DialogHelpComponent, {
                  panelClass: 'dialog-help-pannel',
                  backdropClass: 'backdrop-invisible',
                  width: '600px',
                  data: {
                    title: this.i18n.transform('users:help_title_addusergroup'),
                    text: this.i18n.transform('users:help_addusergroup'),
                    renderAsHtml: true
                  }
                });
  }
}


